import { useMemo, useState } from "react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TotemWithRelations } from "~/services/totemService";

import { MoveTotemsDialog } from "./MoveTotemsDialog";
import {
  getBucketForStatus,
  TOTEM_BUCKETS,
  TotemBucketKey,
  WAREHOUSE_BUCKET_KEYS,
} from "../utils/buckets";

interface BucketTotemsTabProps {
  totems: TotemWithRelations[];
  isLoading?: boolean;
  // Switch to the "Por Cliente" tab — the "En Cliente" bucket routes here so
  // moves out of a client stay client-scoped instead of a bulk aggregate move.
  onGoToClientView: () => void;
}

export function BucketTotemsTab({
  totems,
  isLoading,
  onGoToClientView,
}: BucketTotemsTabProps) {
  const [selectedBucket, setSelectedBucket] = useState<TotemBucketKey | null>(
    null
  );

  // Group totems by bucket in a single pass, preserving TOTEM_BUCKETS order.
  // `items` is the full bucket (drives the count + Mover).
  const rows = useMemo(() => {
    const byBucket = new Map<TotemBucketKey, TotemWithRelations[]>();
    for (const bucket of TOTEM_BUCKETS) byBucket.set(bucket.key, []);
    for (const totem of totems) {
      byBucket.get(getBucketForStatus(totem.status))!.push(totem);
    }
    return TOTEM_BUCKETS.map((bucket) => ({
      bucket,
      items: byBucket.get(bucket.key)!,
    }));
  }, [totems]);

  const selected = rows.find((r) => r.bucket.key === selectedBucket);

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Tótems por Estado</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead className="w-[120px] text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ bucket, items }) => {
                  // "En Cliente" has no bulk target — its "Mover" routes to the
                  // "Por Cliente" tab so the move stays scoped to a specific client.
                  const isClientBucket = bucket.key === "en_cliente";
                  const canMove = isClientBucket
                    ? items.length > 0
                    : bucket.target !== null && items.length > 0;
                  return (
                    <TableRow
                      key={bucket.key}
                      className="bg-muted/60 font-bold"
                    >
                      <TableCell>{bucket.label}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {items.length}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          disabled={!canMove}
                          onClick={() =>
                            isClientBucket
                              ? onGoToClientView()
                              : setSelectedBucket(bucket.key)
                          }
                        >
                          Mover
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <MoveTotemsDialog
        source={
          selected
            ? {
                statuses: selected.bucket.statuses,
                count: selected.items.length,
                label: selected.bucket.label,
              }
            : null
        }
        destinations={[
          ...WAREHOUSE_BUCKET_KEYS.filter((k) => k !== selectedBucket),
          "en_cliente",
        ]}
        open={selectedBucket !== null}
        onOpenChange={(o) => !o && setSelectedBucket(null)}
      />
    </>
  );
}
